import { useState } from "react";
import { Link2, Play, Search } from "lucide-react";
import type { VideoState, VideoType } from "@/lib/room-state";
import { detectVideoType } from "@/lib/video-utils";

interface VideoUrlBarProps {
  videoState: VideoState;
  canControl: boolean;
  onOpenSearch: () => void;
  onLoadVideo: (url: string, type: VideoType) => void;
}

export default function VideoUrlBar({ videoState, canControl, onOpenSearch, onLoadVideo }: VideoUrlBarProps) {
  const [url, setUrl] = useState("");
  function handleLoad(customUrl?: string, customType?: VideoType) {
    const targetUrl = (customUrl || url).trim();
    if (!targetUrl || !canControl) return;
    const type = customType || detectVideoType(targetUrl);
    onLoadVideo(targetUrl, type);
    setUrl("");
  }
  return (
    <div className="h-11 flex items-center gap-1 px-3">
      {/* Browse — icon directly on glass, no container */}
      <button
        onClick={onOpenSearch}
        className="h-8 w-8 flex items-center justify-center rounded-full text-muted-foreground hover:text-[#78B4FF] hover:bg-white/[0.04] transition-all active:scale-95 shrink-0"
        title="Browse YouTube"
        aria-label="Browse YouTube"
      >
        <Search className="w-4 h-4" />
      </button>

      {/* URL input — transparent, no separate container, blends into glass strip */}
      <div className="flex-1 min-w-0 flex items-center gap-2 px-3 h-8">
        <Link2 className="w-3.5 h-3.5 text-muted-foreground/60 shrink-0" />
        <input
          type="text"
          value={url}
          onChange={(e) => setUrl(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && handleLoad()}
          placeholder={canControl ? "Paste YouTube, HLS, Twitch, Vimeo or MP4…" : "Only host can load"}
          disabled={!canControl}
          className="flex-1 bg-transparent text-[13px] text-foreground placeholder:text-muted-foreground/50 disabled:opacity-40 border-none outline-none min-w-0"
        />
        {videoState.url && (
          <span className="hidden md:inline-flex text-[9px] font-mono font-semibold uppercase tracking-wider text-muted-foreground/60 shrink-0">
            {videoState.type}
          </span>
        )}
      </div>

      {/* Load — integrated control, transparent until enabled */}
      <button
        onClick={() => handleLoad()}
        disabled={!url.trim() || !canControl}
        className="h-8 px-3.5 flex items-center gap-1.5 text-xs font-medium rounded-full text-foreground hover:bg-white/[0.04] hover:text-[#78B4FF] disabled:opacity-30 disabled:hover:text-foreground transition-all active:scale-95 shrink-0"
      >
        <Play className="w-3.5 h-3.5 fill-current" />
        <span>Load</span>
      </button>
    </div>
  );
}
